/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import Swal from 'sweetalert2'
import { axiosClientAuth } from '../../utils'
import { useAuth } from '../../modules/auth'
import ModalEditRole from '../../../_payairo/partials/widgets/_new/modal/ModalEditRole'
import ModalViewRole from '../../../_payairo/partials/widgets/_new/modal/ModalViewRole'
import { PageLink, PageTitle, useLayout } from '../../../_payairo/layout/core'
import { useThemeMode } from '../../../_payairo/partials/layout/theme-mode/ThemeModeProvider'
import { getDashboardPath, isManagerRole } from '../../utils/helper'

type RoleUser = {
  uuid: string
  email: string
  user_type: string
  is_active: boolean
  created_at: string
}

const RolesManagement: FC = () => {
  const [editRoleShow, setEditRoleShow] = useState(false)
  const [viewRoleShow, setViewRoleShow] = useState(false)
  const [currData, setCurrData] = useState<any>(null)
  const [data, setData] = useState<any[]>([])

  const { checkPermission, currentUser, auth } = useAuth()
  const { setRefreshCustomerSupportUsers } = useLayout()

  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const userType = currentUser?.user_type || auth?.user_type
  const isCustomerSupportUser =
    role === 'CUSTOMER_SUPPORT' || role === 'CustomerSupport' || userType === 'CustomerSupport'
  const isSpectatorUser = role === 'Spectator' || userType === 'Spectator'
  const isManagerUser = isManagerRole(role, userType)
  /** Admin / Super Admin / Developer: full permission-group UI. Manager: staff creation + list only. */
  const isAdminPermissionUi = !isCustomerSupportUser && !isSpectatorUser && !isManagerUser

  const { mode } = useThemeMode()
  const isDark = mode === 'dark' || (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  // 🔹 Fetch Roles
  const getData = async () => {
    try {
      const res = await axiosClientAuth.get('/auth/api-permission-group')
      setData(res.data?.data || [])
    } catch (error) {
      console.error('Error fetching roles:', error)
    }
  }

  const [roleUsers, setRoleUsers] = useState<RoleUser[]>([])
  const [csLoading, setCsLoading] = useState(false)
  const [csError, setCsError] = useState<string | null>(null)

  // 🔹 Fetch Specific Role Users
  const fetchRoleUsers = async (endpoint: string, setUsers: any, setLoading: any, setError: any) => {
    try {
      setLoading(true)
      setError(null)
      const res = await axiosClientAuth.get(endpoint)
      if (res.data?.status || Array.isArray(res.data?.data)) {
        const data = res.data?.data || []
        // Sort descending by created_at
        data.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        setUsers(data)
      } else {
        setError(res.data?.message || `Failed to load users from ${endpoint}`)
        setUsers([])
      }
    } catch (error: any) {
      console.error(`Error fetching ${endpoint}:`, error)
      setError(error?.response?.data?.message || `Failed to load users for route ${endpoint}`)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const loadAllRoleUsers = () => {
    fetchRoleUsers('/auth/useradmin/all-customer-support/', setRoleUsers, setCsLoading, setCsError)
  }

  const refreshRolePageData = () => {
    loadAllRoleUsers()
    if (isAdminPermissionUi) {
      getData()
    }
  }

  useEffect(() => {
    if (isAdminPermissionUi) {
      getData()
    }
    loadAllRoleUsers()
  }, [isAdminPermissionUi])

  useEffect(() => {
    // Register real callback (not wrapped), so Add Role modal can refresh table instantly.
    setRefreshCustomerSupportUsers?.(refreshRolePageData)
    return () => setRefreshCustomerSupportUsers?.(() => { })
  }, [setRefreshCustomerSupportUsers, isAdminPermissionUi])

  // 🔹 Delete Role
  const deleteRole = (uuid: string) => {
    Swal.fire({
      titleText: 'Are you sure?',
      text: "This action can't be reverted",
      icon: 'warning',
      confirmButtonText: 'Yes, delete',
      showCancelButton: true,
      cancelButtonText: 'No',
    }).then(async (action) => {
      if (action.isConfirmed) {
        try {
          await axiosClientAuth.delete(
            `/auth/api-permission-group?uuid=${uuid}`
          )

          Swal.fire({
            titleText: 'Success!',
            text: 'Role deleted successfully',
            icon: 'success',
            confirmButtonText: 'Ok',
          }).then(() => getData())
        } catch (error) {
          console.error('Delete role error:', error)
        }
      }
    })
  }

  const getRoleBadgeClass = (userType?: string) => {
    const t = String(userType || '').toLowerCase()
    if (t === 'manager') return 'badge-light-warning text-warning'
    if (t === 'spectator') return 'badge-light-info text-info'
    if (t === 'customersupport' || t === 'customer_support') return 'badge-light-primary text-primary'
    return 'badge-light-secondary text-gray-700'
  }

  // 🔹 Reusable Table Component
  const RoleUsersTable = ({ title, users, loading, error }) => (
    <div className='col-12 mb-8'>
      <div className='card card-flush overflow-hidden'>
        <div className='card-header border-bottom-0 pt-8 pb-3 px-8'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-2 text-gray-800'>
              {title}
            </span>
            <span className='fw-semibold fs-7 text-muted'>
              Total {users.length} Accounts
            </span>
          </h3>
        </div>

        <div className='card-body pt-0 px-8 pb-8'>
          {error && (
            <div className='alert alert-danger d-flex align-items-center p-5 mb-5 rounded border-danger border-dashed'>
              <i className='fas fa-exclamation-circle fs-2hx text-danger me-4' />
              <div className='d-flex flex-column'>
                <h5 className='mb-1 text-danger'>Notice</h5>
                <span className='text-danger opacity-75'>{error}</span>
              </div>
            </div>
          )}

          {loading ? (
            <div className='text-center py-10'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className='text-center py-10'>
              <i className='fas fa-users fs-3x text-gray-400 mb-5' />
              <p className='fw-bold fs-5 text-gray-600 mb-2'>No accounts found</p>
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table align-middle table-row-dashed fs-6 gy-5 table-hover'>
                <thead>
                  <tr className='text-start text-gray-500 fw-bold fs-7 text-uppercase gs-0 border-bottom border-gray-200'>
                    <th className='min-w-150px pb-3'>Email</th>
                    <th className='min-w-150px pb-3'>Role</th>
                    <th className='min-w-100px pb-3'>Status</th>
                    <th className='min-w-150px pb-3 text-end'>Created</th>
                  </tr>
                </thead>
                <tbody className='fw-semibold text-gray-600'>
                  {users.map((user: any) => (
                    <tr key={user.uuid || user.id || user.email} className='border-bottom border-gray-200 transition-all hover-bg-light-primary'>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='symbol symbol-35px me-3'>
                            <div className='symbol-label fw-bold fs-6 bg-light-primary text-primary'>
                              {(user.email?.charAt?.(0) || '?').toUpperCase()}
                            </div>
                          </div>
                          <span className='text-gray-800 fw-bold fs-6 d-block'>
                            {user.email}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge px-3 py-2 fs-7 fw-bold ${getRoleBadgeClass(user.user_type)}`}>
                          {user.user_type || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge px-3 py-2 fs-7 fw-bold ${(user.is_active === true || user.status === 'Active')
                              ? 'badge-light-success text-success'
                              : 'badge-light-danger text-danger'
                            }`}
                        >
                          {(user.is_active === true || user.status === 'Active') ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className='text-end'>
                        {user.created_at ? moment(user.created_at).format('DD MMM YYYY') : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )



  return (
    <>
      {/* Permission groups — Admin scope only (not Manager) */}
      {isAdminPermissionUi && (
        <div className='mb-10'>
          <h2 className='fw-bold fs-3 mb-5 text-gray-800'>Roles</h2>
          <div className='row'>
            {data.map((ele) => (
              <div key={ele.uuid} className='col-md-4'>
                <div className='card card-flush h-md-100'>
                  <div className='card-header'>
                    <div className='card-title'>
                      <h2 className='text-gray-800'>{ele.name}</h2>
                    </div>
                  </div>

                  <div className='card-body pt-1'>
                    <div className='fw-bold mb-5 text-gray-600'>
                      Total users with this role: {ele.total_users}
                    </div>

                    <div className='d-flex flex-column text-gray-600'>
                      {ele?.available_permissions?.map((permission: any) => (
                        <div
                          key={permission?.permission?.uuid}
                          className='d-flex align-items-center py-2'
                        >
                          <span className='bullet bg-primary me-3' />
                          {permission?.permission?.name}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='card-footer flex-wrap pt-0'>
                    <button
                      className='btn btn-light btn-active-primary my-1 me-2'
                      onClick={() => {
                        setCurrData(ele)
                        setViewRoleShow(true)
                      }}
                    >
                      View Role
                    </button>

                    {checkPermission('Roles Management', 'can_edit') && (
                      <>
                        <button
                          className='btn btn-light btn-active-light-primary my-1 me-2'
                          onClick={() => {
                            setCurrData(ele)
                            setEditRoleShow(true)
                          }}
                        >
                          Edit Role
                        </button>

                        <button
                          className='btn btn-light btn-active-primary my-1 me-2'
                          onClick={() => deleteRole(ele.uuid)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdminPermissionUi && (
        <ModalEditRole
          show={editRoleShow}
          setShow={setEditRoleShow}
          curr={currData}
        />
      )}
      {isAdminPermissionUi && (
        <ModalViewRole
          show={viewRoleShow}
          setShow={setViewRoleShow}
          curr={currData}
        />
      )}

      {/* Staff Accounts Separated Tables Section */}
      <div className='row mt-8'>
        <RoleUsersTable
          title={isManagerUser ? 'Staff Users (Spectator, Customer Support)' : 'Staff Users (Manager, Spectator, Customer Support)'}
          users={roleUsers}
          loading={csLoading}
          error={csError}
        />
      </div>
    </>
  )
}


/* 🔹 Wrapper */
const RolesManagementWrapper: FC = () => {
  const intl = useIntl()
  const { setRightTolbar } = useLayout()
  const { currentUser, auth } = useAuth()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const userType = currentUser?.user_type || auth?.user_type
  const homePath = getDashboardPath(role, userType)

  const myBreadCrumbs: Array<PageLink> = [
    { title: 'Home', path: homePath, isSeparator: false, isActive: false },
    { title: 'Roles Management', path: '', isSeparator: true, isActive: true },
  ]

  useEffect(() => {
    setRightTolbar('rolesmanagement')
  }, [])

  return (
    <>
      <PageTitle breadcrumbs={myBreadCrumbs}>
        {intl.formatMessage({ id: 'Roles Management' })}
      </PageTitle>
      <RolesManagement />
    </>
  )
}

export { RolesManagementWrapper }
